import React from "react";
import { Link, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Trash2, Minus, Plus, ArrowRight, ShoppingBag } from "lucide-react";

export default function Cart() {
  const { brand } = useOutletContext();
  const { brand: key } = useParams();
  const { items, updateQty, remove, subtotal } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const dark = brand.key === "blackpekoe";
  const fg = dark ? "#F5F2EB" : "#2D2622";
  const muted = dark ? "rgba(245,242,235,0.7)" : "#5C5753";
  const subtle = dark ? "rgba(245,242,235,0.55)" : "#8C8581";
  const cardBg = dark ? "rgba(255,255,255,0.04)" : "white";
  const cardBorder = dark ? "rgba(255,255,255,0.08)" : "rgba(45,38,34,0.1)";

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 text-center" style={{ color: fg }}>
        <ShoppingBag className="w-12 h-12 mx-auto" style={{ color: brand.primary }} />
        <h1 className="font-heading font-bold text-2xl mt-4">Your cart is empty</h1>
        <p className="mt-2" style={{ color: muted }}>Add some {brand.name} items to get started.</p>
        <Link to={`/b/${key}/shop`}>
          <Button data-testid="empty-cart-shop" className="mt-6 text-white" style={{ background: brand.primary }}>Browse catalog</Button>
        </Link>
      </div>
    );
  }

  const tax = +(subtotal * 0.05).toFixed(2);
  const delivery = subtotal >= 1000 ? 0 : 40;
  const total = +(subtotal + tax + delivery).toFixed(2);

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-8 py-10" style={{ color: fg }}>
      <p className="overline" style={{ color: subtle }}>{brand.name} • Your cart</p>
      <h1 className="font-heading font-bold text-3xl mt-2 mb-8">Review &amp; checkout</h1>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4">
          {items.map((it) => (
            <div key={it.product_id} data-testid={`cart-item-${it.product_id}`}
              className="rounded-xl p-4 flex gap-4" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
              <img src={it.image_url} alt={it.name} className="w-24 h-24 rounded-lg object-cover" />
              <div className="flex-1 flex flex-col">
                <div className="flex justify-between gap-3">
                  <div>
                    <h3 className="font-heading font-semibold">{it.name}</h3>
                    <p className="text-xs mt-0.5" style={{ color: subtle }}>₹{it.price.toFixed(2)} / {it.unit}</p>
                  </div>
                  <button data-testid={`remove-item-${it.product_id}`} onClick={() => remove(it.product_id)} className="p-1 transition" style={{ color: subtle }}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="mt-auto flex items-center justify-between">
                  <div className="flex items-center rounded-md overflow-hidden" style={{ border: `1px solid ${cardBorder}` }}>
                    <button data-testid={`qty-dec-${it.product_id}`} onClick={() => updateQty(it.product_id, it.quantity - 1)} className="px-3 py-1.5">
                      <Minus className="w-3 h-3" />
                    </button>
                    <span className="px-3 text-sm font-semibold w-10 text-center" data-testid={`qty-${it.product_id}`}>{it.quantity}</span>
                    <button data-testid={`qty-inc-${it.product_id}`} onClick={() => updateQty(it.product_id, it.quantity + 1)} className="px-3 py-1.5">
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                  <p className="font-heading font-bold">₹{(it.price * it.quantity).toFixed(2)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <aside className="rounded-xl p-6 h-fit sticky top-24" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
          <h3 className="font-heading font-semibold text-lg">Order summary</h3>
          <div className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between"><span style={{ color: muted }}>Subtotal</span><span data-testid="summary-subtotal" className="font-semibold">₹{subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between"><span style={{ color: muted }}>Tax (GST 5%)</span><span>₹{tax.toFixed(2)}</span></div>
            <div className="flex justify-between"><span style={{ color: muted }}>Delivery</span><span>{delivery === 0 ? "Free" : `₹${delivery.toFixed(2)}`}</span></div>
            {delivery > 0 && <p className="text-xs mt-1" style={{ color: brand.accent }}>Add ₹{(1000 - subtotal).toFixed(2)} more for free delivery.</p>}
          </div>
          <div className="mt-4 pt-4 flex justify-between items-baseline" style={{ borderTop: `1px solid ${cardBorder}` }}>
            <span className="font-heading font-semibold">Total</span>
            <span data-testid="summary-total" className="font-heading font-bold text-2xl" style={{ color: brand.primary }}>₹{total.toFixed(2)}</span>
          </div>
          <Button
            data-testid="checkout-btn"
            onClick={() => user ? nav(`/b/${key}/checkout`) : nav(`/b/${key}/login`, { state: { from: `/b/${key}/checkout` } })}
            className="w-full mt-6 text-white" style={{ background: brand.primary }}
          >
            {user ? "Proceed to checkout" : "Sign in to checkout"} <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </aside>
      </div>
    </div>
  );
}
